import { enqueueSnackbar } from 'notistack';
import { TFunction } from 'i18next';
import { EvacZone, EvacZoneStatus } from 'shared/types';
import { getTimeAgo } from 'shared/dates';
import { statusSortingComparator } from 'features/evacZones/utils/statusSortingComparator';
import { GridFilterModel, GridApiCommon } from '@mui/x-data-grid';
import {
  EvacZonesFilter,
  EvacZoneWithStatus,
  FiltersType,
} from 'features/evacZones/types';
import {
  EVAC_ZONE_LABEL_TRANS_KEYS,
  EvacZoneStyles,
} from '../../../../constants';

export const notifyOfChanges = (t: TFunction, updated: number): void => {
  if (updated > 0) {
    enqueueSnackbar(
      t('geoEventEvacuations.evacZones.selectionBar.statusesChanged', {
        count: updated,
      }),
      { variant: 'success' },
    );
  } else {
    enqueueSnackbar(
      t('geoEventEvacuations.evacZones.selectionBar.noStatusChanges'),
      { variant: 'info' },
    );
  }
};

export const zoneHasUpdates = (zone: EvacZone): boolean => {
  return zone.status !== zone.externalStatus?.mapped;
};

export const formatTimeAgo = (dateModified: string): string => {
  return getTimeAgo(dateModified)
    .replace('about ', '')
    .replace('hours', 'hr')
    .replace('hour', 'hr')
    .replace('minutes', 'min')
    .replace('minute', 'min')
    .replace('seconds', 'sec')
    .replace('second', 'sec');
};

// Helper functions for comparing different zone attributes
const zoneHasUpdateComparator = (zone: EvacZone): number =>
  zone.status === zone.externalStatus?.mapped ? 1 : 0;

const externalStatusComparator = (zone: EvacZone): EvacZoneStatus | undefined =>
  zone.externalStatus?.mapped;

const internalStatusComparator = (zone: EvacZone): EvacZoneStatus | undefined =>
  zone.status;

const nameComparator = (zone: EvacZone): string =>
  zone.displayName.toLowerCase();

export const compareZonesByUpdatesAndStatus = (
  zone1: EvacZone,
  zone2: EvacZone,
): number => {
  // First sort by most recently updated (newest first)
  const time1 = zone1.dateModified ? new Date(zone1.dateModified).getTime() : 0;
  const time2 = zone2.dateModified ? new Date(zone2.dateModified).getTime() : 0;
  const dateComparison = time2 - time1;
  if (dateComparison !== 0) return dateComparison;

  // Then prioritize zones with updates (external status ≠ internal status)
  const issueComparison =
    zoneHasUpdateComparator(zone1) - zoneHasUpdateComparator(zone2);
  if (issueComparison !== 0) return issueComparison;

  // Then by external status
  const externalComparison = statusSortingComparator(
    externalStatusComparator(zone1),
    externalStatusComparator(zone2),
  );
  if (externalComparison !== 0) return externalComparison;

  // Then by internal status
  const internalComparison = statusSortingComparator(
    internalStatusComparator(zone1),
    internalStatusComparator(zone2),
  );
  if (internalComparison !== 0) return internalComparison;

  // Finally by name
  return nameComparator(zone1).localeCompare(nameComparator(zone2));
};

export const groupZonesByStatus = (
  zones: Array<{ zone: EvacZone; status?: { status: EvacZoneStatus } }>,
  t: TFunction,
): Record<string, string[]> => {
  return zones.reduce<Record<string, string[]>>((acc, zone) => {
    const statusKey = zone.status?.status;

    if (statusKey) {
      const zoneStyle =
        zone.zone.region.evacZoneStyle || EvacZoneStyles.default;
      const status = t(EVAC_ZONE_LABEL_TRANS_KEYS[zoneStyle][statusKey]);
      if (!acc[status]) {
        acc[status] = [];
      }
      acc[status].push(zone.zone.displayName);
    }

    return acc;
  }, {});
};

export const getFooterText = (
  totalZonesLength: number,
  filteredZonesLength: number,
  selectedCount: number,
  t: TFunction,
): string => {
  const isFiltered = filteredZonesLength !== totalZonesLength;
  const hasSelection = selectedCount > 0;

  if (!isFiltered) {
    // No filtering applied
    return hasSelection
      ? t('geoEventEvacuations.evacZones.table.selectedTotal', {
          total: totalZonesLength,
          selected: selectedCount,
        })
      : t('geoEventEvacuations.evacZones.table.total', {
          total: totalZonesLength,
        });
  }

  // Filtering is applied
  return hasSelection
    ? t('geoEventEvacuations.evacZones.table.selectedFiltered', {
        visible: filteredZonesLength,
        total: totalZonesLength,
        selected: selectedCount,
      })
    : t('geoEventEvacuations.evacZones.table.filtered', {
        visible: filteredZonesLength,
        total: totalZonesLength,
      });
};

export const applyAllFilters = (
  apiRef: React.MutableRefObject<GridApiCommon | null>,
  filters: FiltersType,
  searchQuery: string,
): void => {
  if (!apiRef.current) {
    return;
  }
  const filterModel: GridFilterModel = {
    items: [],
    quickFilterValues: searchQuery.trim() ? [searchQuery] : [],
  };

  // Add hasUpdates filter
  if (filters.hasUpdates) {
    filterModel.items.push({
      field: 'hasUpdates',
      operator: 'is',
      value: true,
    });
  }

  // Add external status filters
  const externalStatuses = Object.keys(filters.external).filter(
    (key) => filters.external[key as keyof typeof filters.external],
  );
  if (externalStatuses.length > 0 && !externalStatuses.includes('all')) {
    filterModel.items.push({
      field: 'externalStatus',
      operator: 'isAnyOf',
      value: externalStatuses,
    });
  }

  // Add internal status filters
  const internalStatuses = Object.keys(filters.internal).filter(
    (key) => filters.internal[key as keyof typeof filters.internal],
  );
  if (internalStatuses.length > 0 && !internalStatuses.includes('all')) {
    filterModel.items.push({
      field: 'internalStatus',
      operator: 'isAnyOf',
      value: internalStatuses,
    });
  }

  apiRef.current.setFilterModel(filterModel);
};

export const formatZonesForClipboard = (
  sortedZones: EvacZoneWithStatus[],
  filter: EvacZonesFilter,
  t: TFunction,
): string | null => {
  // filter zones
  const matchingZones = sortedZones.filter(({ zone, status }) => {
    return status?.status && filter(zone, status?.status);
  });

  // no zones match the filter
  if (matchingZones.length === 0) {
    return null;
  }

  // group zones by status
  const groups = groupZonesByStatus(matchingZones, t);

  // format zones for clipboard
  return Object.entries(groups)
    .map(([status, zones]) => `${status}\n${zones.join(', ')}`)
    .join('\n\n');
};

export const copyZones = (
  filter: EvacZonesFilter,
  sortedZones: EvacZoneWithStatus[],
  t: TFunction,
): void => {
  const zoneText = formatZonesForClipboard(sortedZones, filter, t);

  if (!zoneText) {
    enqueueSnackbar(t('geoEventEvacuations.evacZones.copyNoZones'), {
      variant: 'error',
    });
    return;
  }

  navigator.clipboard.writeText(zoneText);
  enqueueSnackbar(t('geoEventEvacuations.evacZones.copySuccess'), {
    variant: 'success',
  });
};
